import { allTags } from '../LearningCenterSearchFilter/data';
import { Suspense, lazy } from 'react';
import { Route, Switch } from 'react-router-dom';

import Spinner from 'components/Spinner';

import NotFoundError from 'pages/NotFoundError';

import {
    APPLICATION_SUBMITTED,
    AVAILABLE_AGENTS_ROUTE,
    CCPA_ROUTE,
    COMPARE_PLANS,
    CONFIRM_EMAIL,
    CONTACT_AGENT_ROUTE,
    CONTACT_US_ROUTE,
    CREATE_ACCOUNT_ROUTE,
    ENROLLMENT_CHECKOUT_ROUTE,
    LEARNING_CENTER_ROUTE,
    MAINTENANCE_ROUTE,
    MANAGE_ACCOUNT_ROUTE,
    MOBILE_TERMS,
    CONTACT_AN_AGENT_ROUTE,
    OUR_COMMITMENT_ROUTE,
    PASSWORD_UPDATED_ROUTE,
    PLANS_ROUTE,
    PLAN_ROUTE,
    PRIVACY_NOTICE_ROUTE,
    REGISTRATION_COMPLETE,
    REQUEST_CALL_BACK,
    REQUEST_CALL_BACK_FORM,
    RESET_PASSWORD_ROUTE,
    SIGN_IN_OIDC_ROUTE,
    SIGN_IN_OIDC_SILENT_ROUTE,
    SIGN_IN_ROUTE,
    SIGN_OUT_OIDC_ROUTE,
    TERMS_ROUTE,
    THANK_YOU_ROUTE,
    VERIFY_EMAIL_ROUTE,
    WELCOME_ROUTE
} from 'utilities/routes';

import styles from './styles.module.scss';

const BaseConfirmationPage = lazy(() =>
    import('components/BaseConfirmationPage')
);
const PasswordUpdatedPage = lazy(() =>
    import('components/PasswordUpdatedPage')
);
const ReduxExample = lazy(() => import('components/ReduxExample'));
const AuthSigninCallback = lazy(() =>
    import('components/functional/auth-signin-callback')
);
const AuthSignoutCallback = lazy(() =>
    import('components/functional/auth-signout-callback')
);
const AuthSilentCallback = lazy(() =>
    import('components/functional/auth-silent-callback')
);
const TempReduxExample = lazy(() => import('temp/components/ReduxExample'));
const PlanDetailsPage = lazy(() => import('temp/pages/PlanDetails'));
const AboutUs = lazy(() => import('pages/AboutUs'));
const AvailableAgents = lazy(() => import('pages/AvailableAgents'));
const ApplicationSubmitted = lazy(() => import('pages/ApplicationSubmitted'));
const CCPA = lazy(() => import('pages/CCPA'));
const ComparePlans = lazy(() => import('pages/ComparePlans'));
const ConfirmEmail = lazy(() => import('pages/ConfirmEmail'));
const ContactAgent = lazy(() => import('pages/ContactAgent'));
const ContactUs = lazy(() => import('pages/ContactUs'));
const EnrollmentCheckoutPage = lazy(() => import('pages/ContinueCheckout'));
const CreateAccount = lazy(() => import('pages/CreateAccount'));
const Home = lazy(() => import('pages/Home'));
const IndividualResource = lazy(() => import('pages/IndividualResource'));
const InternalServerError = lazy(() => import('pages/InternalServerError'));
const LearningCenter = lazy(() => import('pages/LearningCenter'));
const ManageAccount = lazy(() => import('pages/ManageAccount'));
const Medicare = lazy(() => import('pages/Medicare'));
const MobileTerms = lazy(() => import('pages/MobileTerms'));
const NeedHelp = lazy(() => import('pages/NeedHelp'));
const Plans = lazy(() => import('pages/Plans'));
const PrivacyNotice = lazy(() => import('pages/PrivacyNotice'));
const RequestCallBack = lazy(() => import('pages/RequestCallBack'));
const RequestCallBackForm = lazy(() => import('pages/RequestCallBackForm'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const ServerLogoutPage = lazy(() => import('pages/ServerLogoutPage'));
const SignIn = lazy(() => import('pages/SignInPage'));
const SiteMaintenance = lazy(() => import('pages/SiteMaintenance'));
const TermsOfService = lazy(() => import('pages/TermsOfService'));
const ThankYou = lazy(() => import('pages/ThankYou'));
const VerifyEmail = lazy(() => import('pages/VerifyEmail'));
const Welcome = lazy(() => import('pages/Welcome'));

const loading = <Spinner />;

const Content = () => {
    const swapLearningCenterComponent = props => {
        const { match } = props;
        const article = match?.params?.articleName;
        const tagName = `${article}`.replaceAll('-', ' ');

        return allTags.find(
            tag => tag?.toLowerCase() === tagName?.toLowerCase()
        ) ? (
            <LearningCenter {...props} />
        ) : (
            <IndividualResource {...props} />
        );
    };

    return (
        <main className={styles.content}>
            <Suspense fallback={loading}>
                <Switch>
                    <Route exact path="/" component={Home} />
                    <Route
                        exact
                        path={AVAILABLE_AGENTS_ROUTE}
                        component={AvailableAgents}
                    />
                    <Route exact path={WELCOME_ROUTE} component={Welcome} />
                    <Route
                        exact
                        path={CONTACT_US_ROUTE}
                        component={ContactUs}
                    />
                    <Route
                        exact
                        path={CONTACT_AGENT_ROUTE}
                        component={ContactAgent}
                    />
                    <Route
                        exact
                        path={PRIVACY_NOTICE_ROUTE}
                        component={PrivacyNotice}
                    />
                    <Route exact path={CCPA_ROUTE} component={CCPA} />
                    <Route
                        exact
                        path={LEARNING_CENTER_ROUTE}
                        component={LearningCenter}
                    />
                    <Route
                        exact
                        path={TERMS_ROUTE}
                        component={TermsOfService}
                    />
                    <Route exact path={MOBILE_TERMS} component={MobileTerms} />
                    <Route
                        exact
                        path={CONTACT_AN_AGENT_ROUTE}
                        component={NeedHelp}
                    />
                    <Route
                        exact
                        path={REQUEST_CALL_BACK}
                        component={RequestCallBack}
                    />
                    <Route
                        exact
                        path={REQUEST_CALL_BACK_FORM}
                        component={RequestCallBackForm}
                    />
                    <Route exact path={PLANS_ROUTE} component={Plans} />
                    <Route
                        exact
                        path={PLAN_ROUTE}
                        component={PlanDetailsPage}
                    />
                    <Route exact path="/guide/:id" component={Medicare} />
                    <Route
                        exact
                        path="/learning-center/:articleName"
                        component={props => swapLearningCenterComponent(props)}
                    />
                    <Route exact path={THANK_YOU_ROUTE} component={ThankYou} />
                    <Route
                        exact
                        path={MAINTENANCE_ROUTE}
                        component={SiteMaintenance}
                    />

                    <Route
                        exact
                        path="/reduxExample"
                        component={ReduxExample}
                    />
                    <Route
                        exact
                        path="/temp/reduxExample"
                        component={TempReduxExample}
                    />
                    <Route
                        exact
                        path={COMPARE_PLANS}
                        component={ComparePlans}
                    />
                    <Route exact path={SIGN_IN_ROUTE} component={SignIn} />
                    <Route
                        exact
                        path={OUR_COMMITMENT_ROUTE}
                        component={AboutUs}
                    />
                    <Route
                        exact
                        path={SIGN_IN_OIDC_ROUTE}
                        component={AuthSigninCallback}
                    />
                    <Route
                        exact
                        path={SIGN_IN_OIDC_SILENT_ROUTE}
                        component={AuthSilentCallback}
                    />
                    <Route
                        exact
                        path={SIGN_OUT_OIDC_ROUTE}
                        component={AuthSignoutCallback}
                    />
                    <Route
                        exact
                        path={CREATE_ACCOUNT_ROUTE}
                        component={CreateAccount}
                    />
                    <Route
                        exact
                        path={MANAGE_ACCOUNT_ROUTE}
                        component={ManageAccount}
                    />
                    <Route
                        exact
                        path={VERIFY_EMAIL_ROUTE}
                        component={VerifyEmail}
                    />
                    <Route
                        exact
                        path={CONFIRM_EMAIL}
                        component={ConfirmEmail}
                    />
                    <Route
                        exact
                        path={REGISTRATION_COMPLETE}
                        component={BaseConfirmationPage}
                    />
                    <Route
                        exact
                        path={RESET_PASSWORD_ROUTE}
                        component={ResetPassword}
                    />
                    <Route
                        exact
                        path={PASSWORD_UPDATED_ROUTE}
                        component={PasswordUpdatedPage}
                    />
                    <Route
                        exact
                        path={ENROLLMENT_CHECKOUT_ROUTE}
                        component={EnrollmentCheckoutPage}
                    />
                    <Route
                        exact
                        path={APPLICATION_SUBMITTED}
                        component={ApplicationSubmitted}
                    />
                    <Route exact path="/logout" component={ServerLogoutPage} />
                    <Route exact path="/500" component={InternalServerError} />

                    <Route path="*">
                        <NotFoundError />
                    </Route>
                </Switch>
            </Suspense>
        </main>
    );
};

export default Content;
